import { useEventListener } from '@vueuse/core';
import { isEqual } from 'lodash-es';
import {
    computed,
    type ComputedRef,
    onBeforeUnmount,
    ref,
    type Ref,
    watch,
} from 'vue';
import { onBeforeRouteLeave } from 'vue-router';

import { UseDialog } from './useDialog';

// Read by the forced-reload path to tell whether edits are pending.
const dirtyCount = ref(0);
export const isAnythingDirty: ComputedRef<boolean> = computed(
    () => dirtyCount.value > 0,
);

export function useDirtyState<T>(
    current: Ref<T | null>,
    original: Ref<T>,
    discardDialog?: UseDialog<boolean, boolean>,
): {
    isDirty: ComputedRef<boolean>;
} {
    const isDirty = computed(() => {
        return (
            original.value &&
            !!current.value &&
            !isEqual(current.value, original.value)
        );
    });

    // Prevent Vue router navigation when dirty (only if dialog is provided)
    if (discardDialog) {
        onBeforeRouteLeave(async (to, from, next) => {
            console.log(current.value, original.value);
            if (isDirty.value) {
                const confirmed = await discardDialog.show(true);
                if (confirmed) {
                    next();
                } else {
                    next(false);
                }
            } else {
                next();
            }
        });
    }

    // Optional beforeunload event listener to warn about unsaved changes
    let beforeUnloadHandler: ((e: BeforeUnloadEvent) => void) | null = null;

    useEventListener(window, 'beforeunload', (e: BeforeUnloadEvent) => {
        if (isDirty.value) {
            e.preventDefault();
            e.returnValue = '';
        }
    });
    beforeUnloadHandler = (e: BeforeUnloadEvent) => {
        e.preventDefault();
        e.returnValue = '';
    };

    watch(isDirty, (newVal) => {
        if (newVal) {
            window.addEventListener('beforeunload', beforeUnloadHandler);
        } else {
            window.removeEventListener('beforeunload', beforeUnloadHandler);
        }
    });

    // Maintain the global dirty count. `counted` guards against double
    // increments and ensures we decrement exactly once on unmount if still
    // dirty, so the count can't leak.
    let counted = false;
    watch(
        isDirty,
        (newVal) => {
            if (newVal && !counted) {
                dirtyCount.value++;
                counted = true;
            } else if (!newVal && counted) {
                dirtyCount.value--;
                counted = false;
            }
        },
        { immediate: true },
    );

    onBeforeUnmount(() => {
        window.removeEventListener('beforeunload', beforeUnloadHandler);
        if (counted) {
            dirtyCount.value--;
            counted = false;
        }
    });

    return {
        isDirty,
    };
}
